import JSZip from "jszip";
import {
  FileText,
  IndianRupee,
  Percent,
  PlusCircle,
  User
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import AffiliatePartnerDashboardContent from "../../components/AdminPortal/AffiliatePartnerDashboardContent";
import LoansVsInsuranceChart from "../../components/AdminPortal/charts/LoansVsInsuranceChart";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import usePieChart from "../../hooks/usePieChart";
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";

export default function AffiliateDashboard() {
  const { sidebarExpanded, isMobile } = useSidebar();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({
    totalPartners: 0,
    activePartners: 0,
    inactivePartners: 0,
    totalApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    totalCommission: 0,
    paidCommission: 0,
    pendingCommission: 0,
    successRate: 0,
    successRateChange: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [error, setError] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    category: "all",
    dateRange: "all",
  });
  const { user } = useAuth();
  const [customerTypePieChartData, setCustomerTypePieChartData] = useState([
    { label: "No Data", value: 0 },
  ]);
  const [affiliateTypePieChartData, setAffiliateTypePieChartData] = useState([
    { label: "No Data", value: 0 },
  ]);
  const [loanPieChartData, setLoanPieChartData] = useState([
    { label: "No Data", value: 0 },
  ]);
  const [loanChartLoading, setLoanChartLoading] = useState(false);
  const [customerChartLoading, setCustomerChartLoading] = useState(false);
  const [affiliateChartLoading, setAffiliateChartLoading] = useState(false);
  const [statsFilter, setStatsFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

useEffect(() => {
  let filtered = partners;

  // Apply search filter
  if (searchQuery.trim() !== "") {
    filtered = filtered.filter(
      (partner) =>
        partner.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.contact?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.branchName?.toLowerCase().includes(searchQuery.toLowerCase()) // ✅ added
    );
  }

  // Apply status filter
  if (filters.status !== "all") {
    filtered = filtered.filter(
      (partner) => partner.status === filters.status
    );
  }

  // Apply category filter
  if (filters.category !== "all") {
    filtered = filtered.filter(
      (partner) => partner.category === filters.category
    );
  }

  // Apply date range filter
  if (filters.dateRange !== "all") {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    filtered = filtered.filter((partner) => {
      const joiningDate = new Date(partner.joiningDate);
      if (filters.dateRange === "last30days") {
        return joiningDate >= thirtyDaysAgo;
      }
      return true;
    });
  }
  console.log(filtered,"filterrrrrrrrr")
  setFilteredPartners(filtered);
}, [searchQuery, partners, filters]);

  useEffect(() => {
    if (partners.length > 0) {
      // Calculate total commission from all partners
      console.log(partners);
      const totalCommission = partners.reduce((sum, partner) => {
        // Convert commission string to number (remove currency symbol and commas)
        const commissionValue = partner.commission
          ? parseFloat(partner.commission.replace(/[^0-9.-]+/g, ""))
          : 0;
        return sum + commissionValue;
      }, 0);

      // Calculate paid commission from all partners
      const paidCommission = partners.reduce((sum, partner) => {
        // Convert paid commission string to number (remove currency symbol and commas)
        const paidValue = partner.commissionDetails?.paid
          ? parseFloat(partner.commissionDetails.paid.replace(/[^0-9.-]+/g, ""))
          : 0;
        return sum + paidValue;
      }, 0);

      // Calculate pending commission (total - paid)
      const pendingCommission = totalCommission - paidCommission;

      // Update stats with calculated commissions
      setStats((prevStats) => ({
        ...prevStats,
        totalCommission,
        paidCommission,
        pendingCommission,
      }));
    }
  }, [partners]);

  // Fetch pie chart data
  useEffect(() => {
    const fetchPieChartData = async () => {
      try {
        // Fetch loan type data
        const loanResponse = await affiliateAPI.getAffiliatePieChartData(
          "loanTypeCounts",
          "all"
        );
        if (loanResponse.success) {
          setLoanPieChartData(loanResponse.data);
        }

        // Fetch customer type data
        const customerResponse = await affiliateAPI.getAffiliatePieChartData(
          "customerTypeCounts",
          "all"
        );
        if (customerResponse.success) {
          setCustomerTypePieChartData(customerResponse.data);
        }

        // Fetch affiliate type data
        const affiliateResponse = await affiliateAPI.getAffiliatePieChartData(
          "affiliateTypeCounts",
          "all"
        );
        if (affiliateResponse.success) {
          setAffiliateTypePieChartData(affiliateResponse.data);
        }
      } catch (err) {
        console.error("Failed to fetch pie chart data", err);
      }
    };

    fetchPieChartData();
  }, []);

  // Handle period change for loan pie chart
  const handleLoanPeriodChange = async (period) => {
    try {
      setLoanChartLoading(true);
      const response = await affiliateAPI.getAffiliatePieChartData(
        "loanTypeCounts",
        period
      );
      if (response.success) {
        setLoanPieChartData(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch loan data for period:", period, err);
    } finally {
      setLoanChartLoading(false);
    }
  };

  // Handle period change for customer type pie chart
  const handleCustomerPeriodChange = async (period) => {
    try {
      setCustomerChartLoading(true);
      const response = await affiliateAPI.getAffiliatePieChartData(
        "customerTypeCounts",
        period
      );
      if (response.success) {
        setCustomerTypePieChartData(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch customer data for period:", period, err);
    } finally {
      setCustomerChartLoading(false);
    }
  };

  // Handle period change for affiliate type pie chart
  const handleAffiliatePeriodChange = async (period) => {
    try {
      setAffiliateChartLoading(true);
      const response = await affiliateAPI.getAffiliatePieChartData(
        "affiliateTypeCounts",
        period
      );
      if (response.success) {
        setAffiliateTypePieChartData(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch affiliate data for period:", period, err);
    } finally {
      setAffiliateChartLoading(false);
    }
  };

  // Pie chart element using the hook
  const loanPieChart = usePieChart({
    data: loanPieChartData,
    colorRange: [40, 50],
    heading: "Loan Types (All Time)",
    defaultPeriod: "all",
    onChange: handleLoanPeriodChange,
    loading: loanChartLoading,
    customHeight: "min-h-[280px]",
  });
  const CustomerTypePieChart = usePieChart({
    data: customerTypePieChartData,
    colorRange: [220, 250], // green shades
    heading: "Customer Types",
    defaultPeriod: "all",
    onChange: handleCustomerPeriodChange,
    loading: customerChartLoading,
  });
  const AffiliateTypePieChart = usePieChart({
    data: affiliateTypePieChartData,
    colorRange: [170, 200], // green shades
    heading: "Affiliate Types",
    defaultPeriod: "all",
    onChange: handleAffiliatePeriodChange,
    loading: affiliateChartLoading,
    customHeight: "min-h-[400px]",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log("Starting to fetch data...");

      // Fetch partners data
      const partnersResponse = await affiliateAPI.getAll();

      // Handle partners data and calculate partner stats
      if (partnersResponse?.success && partnersResponse?.data?.partners) {
        const partnersArray = partnersResponse.data.partners.map((partner) => ({
          id: partner._id,
          name: partner.name,
          contact: partner.contact,
          joiningDate: partner.joiningDate,
          category: partner.category,
          applications: partner.applications,
          applicationSuccess: partner.applicationSuccess,
          applicationSuccessCount: partner.applicationSuccessCount,
          commission: partner.commission,
          status: partner.status,
          isStarPartner: partner.isStarPartner,
          email: partner.email,
          accountDetails: partner.accountDetails || {},
          commissionDetails: partner.commissionDetails || {},
          documents: partner.documents || [],
          branchName: partner.branchName,
        }));

        // Calculate and update partner-related stats
        const totalPartners = partnersArray.length;
        const activePartners = partnersArray.filter(
          (partner) => partner.status === "Active"
        ).length;
        const inactivePartners = partnersArray.filter(
          (partner) => partner.status === "Inactive"
        ).length;

        setPartners(partnersArray);
        setStats((prevStats) => ({
          ...prevStats,
          totalPartners,
          activePartners,
          inactivePartners,
        }));
      } else {
        console.warn("Invalid partners data structure:", partnersResponse);
        setPartners([]);
      }

      // Fetch and handle application/commission stats
      const statsResponse = await affiliateAPI.getStats();

      if (statsResponse?.success && statsResponse?.data?.stats) {
        const stats = statsResponse.data.stats;

        // Update only application and commission related stats
        setStats((prevStats) => ({
          ...prevStats,
          totalApplications: stats.totalApplications || 0,
          approvedApplications: stats.approvedApplications || 0,
          rejectedApplications: stats.rejectedApplications || 0,
          totalCommission: stats.totalCommission || 0,
          paidCommission: stats.paidCommission || 0,
          pendingCommission: stats.pendingCommission || 0,
          successRate: stats.successRate || 0,
          successRateChange: stats.successRateChange || 0,
        }));
      } else {
        console.warn("Invalid stats data structure:", statsResponse);
      }

      setError(null);
    } catch (err) {
      console.error("Error in fetchData:", err);
      setError("Failed to load affiliate partners. Please try again later.");
      setPartners([]);
      // Add a retry mechanism
      setTimeout(() => {
        fetchData();
      }, 5000); // Retry after 5 seconds
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // The search is now handled by the useEffect above
  };

  const handleExport = async () => {
    console.log("BUtton is clicking !!");

    // Determine which partners to export
    let partnersToExport;
    if (selectedPartners.length > 0) {
      // Export only selected partners from the currently filtered list
      partnersToExport = filteredPartners.filter((partner) =>
        selectedPartners.includes(partner.id)
      );
    } else {
      // If no partners are selected, export all currently filtered partners
      partnersToExport = filteredPartners;
    }

    if (partnersToExport.length === 0) {
      toast.error(
        "No partners to export. Please adjust filters or selections."
      );
      return;
    }

    try {
      setLoading(true);

      const partnerDetailsData = partnersToExport.map((p) => {
        const totalCommissionNum = p.commission
          ? parseFloat(String(p.commission).replace(/[^0-9.-]+/g, ""))
          : 0;
        const paidCommissionNum = p.commissionDetails?.paid
          ? parseFloat(
              String(p.commissionDetails.paid).replace(/[^0-9.-]+/g, "")
            )
          : 0;
        const pendingCommissionNum =
          p.commissionDetails?.pending !== undefined &&
          p.commissionDetails?.pending !== null
            ? parseFloat(
                String(p.commissionDetails.pending).replace(/[^0-9.-]+/g, "")
              )
            : totalCommissionNum - paidCommissionNum;

        return {
          "Partner ID": p.id,
          Name: p.name,
          Email: p.email,
          Contact: p.contact,
          "Joining Date": p.joiningDate,
          Category: p.category,
          Status: p.status,
          "Star Partner": p.isStarPartner ? "Yes" : "No",
          "Bank Name": p.accountDetails?.bankName || "N/A",
          "Account Number": p.accountDetails?.accountNumber || "N/A",
          "IFSC Code": p.accountDetails?.ifscCode || "N/A",
          "Total Applications (Raw)": p.applications,
          "Successful Applications Count": p.applicationSuccessCount,
          "Success Rate (Raw)": p.applicationSuccess,
          "Total Commission (Partner)": formatCurrency(totalCommissionNum),
          "Paid Commission (Partner)": formatCurrency(paidCommissionNum),
          "Pending Commission (Partner)": formatCurrency(pendingCommissionNum),
        };
      });

      let totalApplicationsSelected = 0;
      let approvedApplicationsSelected = 0;
      let totalCommissionSelected = 0;
      let paidCommissionSelected = 0;

      partnersToExport.forEach((p) => {
        const appsMatch = String(p.applications).match(/^(\d+)/);
        const currentPartnerApplications = appsMatch
          ? parseInt(appsMatch[1], 10)
          : 0;
        totalApplicationsSelected += currentPartnerApplications;

        approvedApplicationsSelected +=
          parseInt(p.applicationSuccessCount) || 0;
        totalCommissionSelected += p.commission
          ? parseFloat(String(p.commission).replace(/[^0-9.-]+/g, ""))
          : 0;
        paidCommissionSelected += p.commissionDetails?.paid
          ? parseFloat(
              String(p.commissionDetails.paid).replace(/[^0-9.-]+/g, "")
            )
          : 0;
      });

      const rejectedApplicationsSelected =
        totalApplicationsSelected - approvedApplicationsSelected;
      const pendingCommissionSelected =
        totalCommissionSelected - paidCommissionSelected;
      const successRateSelected =
        totalApplicationsSelected > 0
          ? (approvedApplicationsSelected / totalApplicationsSelected) * 100
          : 0;

      const partnerStatsData = [
        { Stat: "Total Selected Partners", Value: partnersToExport.length },
        { Stat: "Total Applications", Value: totalApplicationsSelected },
        { Stat: "Approved Applications", Value: approvedApplicationsSelected },
        { Stat: "Rejected Applications", Value: rejectedApplicationsSelected },
        {
          Stat: "Overall Success Rate",
          Value: `${successRateSelected.toFixed(2)}%`,
        },
        {
          Stat: "Total Commission Earned",
          Value: formatCurrency(totalCommissionSelected),
        },
        {
          Stat: "Total Commission Paid",
          Value: formatCurrency(paidCommissionSelected),
        },
        {
          Stat: "Total Commission Pending",
          Value: formatCurrency(pendingCommissionSelected),
        },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(partnerDetailsData),
        "Partner Details"
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(partnerStatsData),
        "Selected Partner Stats"
      );

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      const zip = new JSZip();
      zip.file("Affiliate_Partners_Data.xlsx", excelBuffer); // General name if not always selected
      const zipBlob = await zip.generateAsync({ type: "blob" });

      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "affiliate_partners_data.zip";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Data for selected partners exported successfully!");
    } catch (err) {
      toast.error("Failed to export data. Please try again.");
      setError("Failed to export data. Please try again.");
      console.error("Export error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPartners([]);
    } else {
      setSelectedPartners(partners.map((partner) => partner.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectPartner = (id) => {
    if (selectedPartners.includes(id)) {
      setSelectedPartners(
        selectedPartners.filter((partnerId) => partnerId !== id)
      );
    } else {
      setSelectedPartners([...selectedPartners, id]);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      console.log("Updating status for partner:", id, "to:", newStatus);

      // Update backend
      const response = await affiliateAPI.updateStatus(id, newStatus);

      if (response.success) {
        // Update frontend state
        setPartners((prevPartners) =>
          prevPartners.map((partner) =>
            partner.id === id ? { ...partner, status: newStatus } : partner
          )
        );

        // Update stats based on new status
        setStats((prevStats) => {
          const updatedStats = { ...prevStats };

          // If status changed from Active to Inactive
          if (newStatus === "Inactive") {
            updatedStats.activePartners = Math.max(
              0,
              prevStats.activePartners - 1
            );
            updatedStats.inactivePartners = prevStats.inactivePartners + 1;
          }
          // If status changed from Inactive to Active
          else if (newStatus === "Active") {
            updatedStats.activePartners = prevStats.activePartners + 1;
            updatedStats.inactivePartners = Math.max(
              0,
              prevStats.inactivePartners - 1
            );
          }

          return updatedStats;
        });

        // Show success message
        toast.success(`Status updated to ${newStatus}`);
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Error in handleStatusUpdate:", err);
      toast.error(err.message || "Failed to update status. Please try again.");

      // Revert the status change in the UI
      setPartners((prevPartners) =>
        prevPartners.map((partner) =>
          partner.id === id
            ? { ...partner, status: partner.status } // Keep original status
            : partner
        )
      );
    }
  };

  const navigateToProfile = (id) => {
    const role = user?.role?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      navigate(`/${role}/affiliate-partners/${id}`);
    } else {
      console.warn("Unknown role:", role);
    }
  };

  const navigateToNewAffiliate = () => {
    const role = user?.role?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      navigate(`/${role}/affiliate-partners/new`);
    } else {
      console.warn("Unknown role:", role);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "₹0";
    return `₹${Number(value).toLocaleString("en-IN")}`;
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${value}%`;
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: "all",
      category: "all",
      dateRange: "all",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#131313] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-[#FFCA20] text-black px-4 py-2 rounded-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#000000] text-white mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="flex flex-col border-b border-gray-800">
          <div className="bg-[#000000] p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center items-center gap-4 text-center">
            <h1 className="text-xl font-semibold">
              Affiliate Partner Management
            </h1>

            <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
              <button
                onClick={navigateToNewAffiliate}
                className="bg-[#FFCA20] text-black px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto"
              >
                <PlusCircle className="w-5 h-5 mr-2" />
                New Affiliate
              </button>

              <button
                onClick={handleExport}
                className="bg-[#FFCA20] text-black px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto"
              >
                <FileText className="w-5 h-5 mr-2" />
                Export Data
              </button>
            </div>
          </div>

          <div>
            <div className="bg-[#000000] border-b border-[#333333]  p-4 pl-8 flex flex-col sm:flex-row justify-between items-start  gap-4 sm:gap-0">
              <div className="tab-strip-border overflow-x-auto mb-[-1rem]">
                <div className="flex space-x-6">
                  <button
                    onClick={() => setActiveTab("dashboard")}
                    className={`tab-button ${
                      activeTab === "dashboard"
                        ? "active-tab-border"
                        : "inactive-tab"
                    }`}
                  >
                    Dashboard
                  </button>

                  <button
                    onClick={() => setActiveTab("partners")}
                    className={`tab-button ${
                      activeTab === "partners"
                        ? "active-tab-border"
                        : "inactive-tab"
                    }`}
                  >
                    Affiliate Partner List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {activeTab === "dashboard" && (
          <>
            <div className="p-4 sm:p-6 flex-1 overflow-auto">
              {/* Filter dropdown */}
              <div className="flex justify-end mb-4">
                <select
                  value={statsFilter}
                  onChange={(e) => setStatsFilter(e.target.value)}
                  className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
                >
                  <option value="all">All Time</option>
                  <option value="yearly">Yearly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {/* Stats Cards */}
              {(() => {
                const multiplier = statsFilter === "monthly" ? 0.3 : statsFilter === "quarterly" ? 0.7 : statsFilter === "yearly" ? 0.9 : 1;
                const filteredStats = {
                  totalPartners: Math.floor((stats.totalPartners || 0) * multiplier),
                  activePartners: Math.floor((stats.activePartners || 0) * multiplier),
                  inactivePartners: Math.floor((stats.inactivePartners || 0) * multiplier),
                  totalApplications: Math.floor((stats.totalApplications || 0) * multiplier),
                  approvedApplications: Math.floor((stats.approvedApplications || 0) * multiplier),
                  rejectedApplications: Math.floor((stats.rejectedApplications || 0) * multiplier),
                  totalCommission: (stats.totalCommission || 0) * multiplier,
                  paidCommission: (stats.paidCommission || 0) * multiplier,
                  pendingCommission: (stats.pendingCommission || 0) * multiplier,
                  successRate: stats.successRate || 0,
                  successRateChange: stats.successRateChange || 0
                };
                
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#131313] p-4 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <div className="flex flex-col gap-2">
                          <h3 className="text-[#F3F3F3] text-sm">TOTAL PARTNERS</h3>
                          <p className="text-2xl font-bold">
                            {filteredStats.totalPartners || 0}
                          </p>
                        </div>
                        <div className="bg-[#FFCA20] w-12 h-12 rounded-full flex items-center justify-center">
                          <User className="text-black w-8 h-8" />
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <div className="mr-4">
                          <p className="text-xs text-green-500">Active</p>
                          <p className="text-green-500">
                            {filteredStats.activePartners || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Inactive</p>
                          <p className="text-gray-400">
                            {filteredStats.inactivePartners || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#131313] p-4 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <div className="flex flex-col gap-2">
                          <h3 className="text-[#F3F3F3] text-sm">TOTAL APPLICATIONS</h3>
                          <p className="text-2xl font-bold">
                            {filteredStats.totalApplications || 0}
                          </p>
                        </div>
                        <div className="bg-[#FFCA20] w-12 h-12 rounded-full flex items-center justify-center">
                          <FileText className="text-black w-8 h-8" />
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <div className="mr-4">
                          <p className="text-xs text-green-500">Approved</p>
                          <p className="text-green-500">
                            {filteredStats.approvedApplications || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-red-500">Rejected</p>
                          <p className="text-red-500">
                            {filteredStats.rejectedApplications || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#131313] p-4 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <div className="flex flex-col gap-2">
                          <h3 className="text-[#F3F3F3] text-sm">TOTAL COMMISSION</h3>
                          <p className="text-2xl font-bold">
                            {formatCurrency(filteredStats.totalCommission)}
                          </p>
                        </div>
                        <div className="bg-[#FFCA20] w-12 h-12 rounded-full flex items-center justify-center">
                          <IndianRupee className="text-black w-8 h-8" />
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <div className="mr-4">
                          <p className="text-xs text-green-500">Paid</p>
                          <p className="text-green-500">
                            {formatCurrency(filteredStats.paidCommission)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#FFCA20]">Pending</p>
                          <p className="text-[#FFCA20]">
                            {formatCurrency(filteredStats.pendingCommission)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#131313] p-4 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <h3 className="text-[#F3F3F3] text-sm">SUCCESS RATE</h3>
                        <div className="bg-[#FFCA20] w-12 h-12 rounded-full flex items-center justify-center">
                          <Percent className="text-black w-8 h-8" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold">
                        {formatPercentage(filteredStats.successRate)}
                      </p>
                      <div className="mt-2">
                        <p className="text-xs text-gray-400">
                           {statsFilter === "all" ? "All Time" : statsFilter === "yearly" ? "Yearly" : statsFilter === "quarterly" ? "Quarterly" : "Monthly"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex flex-col lg:flex-row justify-center items-stretch gap-4 w-full mt-6 mb-6">
                <div className="flex-1">{loanPieChart}</div>
                <div className="flex-1">{CustomerTypePieChart}</div>
              </div>
              <div className="flex flex-col lg:flex-row justify-center items-stretch gap-4 w-full mt-6 mb-6">
                <div className="flex-1 overflow-x-auto scrollbar-hide md:overflow-x-visible">
                  <div className="min-w-[500px] md:min-w-full">
                    <LoansVsInsuranceChart />
                  </div>
                </div>
                <div className="flex-1">{AffiliateTypePieChart}</div>
              </div>
            </div>
          </>
        )}
        {activeTab === "partners" && (
          <AffiliatePartnerDashboardContent
            stats={stats}
            formatCurrency={formatCurrency}
            formatPercentage={formatPercentage}
            showFilterPopup={showFilterPopup}
            setShowFilterPopup={setShowFilterPopup}
            filters={filters}
            handleFilterChange={handleFilterChange}
            partners={partners}
            resetFilters={resetFilters}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectAll={selectAll}
            handleSelectAll={handleSelectAll}
            selectedPartners={selectedPartners}
            handleSelectPartner={handleSelectPartner}
            filteredPartners={filteredPartners}
            loading={loading}
            navigateToProfile={navigateToProfile}
          />
        )}
      </div>
    </div>
  );
}
